import React, { useState, useEffect } from 'react';
import { getApiProviderNames, getProviderAliases } from '../../../../Infrastructure/Services/ProviderService';

/* ─── Searchable dropdown (self-contained copy — see note below) ─── */
function SearchableSelect({ value, onChange, disabled, options, placeholder = '— Select —', labelKey = 'label', valueKey = 'value' }) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const filtered = options.filter(o =>
    String(o[labelKey]).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => String(o[valueKey]).toLowerCase() === String(value).toLowerCase())?.[labelKey];

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 180 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', padding: '5px 30px 5px 10px', fontSize: 13,
          border: '1px solid #dee2e6', borderRadius: 4,
          background: disabled ? '#e9ecef' : '#fff',
          color: value ? '#2e2e31' : '#6c757d',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left', position: 'relative',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel ?? (value || placeholder)}
        </span>
        <span style={{ fontSize: 10, color: '#6c757d', flexShrink: 0, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2000,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
          minWidth: 220,
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12 }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{
                padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                color: '#6c757d',
                background: !value ? '#f8f9fa' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? '#f8f9fa' : 'transparent'}
            >
              {placeholder}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No results</div>
            )}
            {filtered.map(o => (
              <div
                key={o[valueKey]}
                onClick={() => { onChange(String(o[valueKey])); setOpen(false); }}
                style={{
                  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  background: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#fdf2f8' : 'transparent',
                  color: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#e20075' : '#2e2e31',
                  fontWeight: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? 600 : 400,
                }}
                onMouseEnter={e => { if (String(o[valueKey]).toLowerCase() !== String(value).toLowerCase()) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#fdf2f8' : 'transparent'; }}
              >
                {o[labelKey]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Provider Pair Selector (API provider ↔ DB provider, alias-aware) ─── */
const PROVIDER_ALIAS_TTL = 15 * 60 * 1000;
const providerAliasCache = {};

export default function ProviderPairSelector({
  aggId, dbProviders, loadingDbProviders,
  providerId, apiProviderName, onChange,
  token, setLoggedIn,
}) {
  const [apiNames, setApiNames] = useState([]);
  const [loadingApiNames, setLoadingApiNames] = useState(false);
  const [matchViaAlias, setMatchViaAlias] = useState(true);
  const [noMatchFound, setNoMatchFound] = useState(false);

  useEffect(() => {
    if (!aggId) { setApiNames([]); return; }
    setLoadingApiNames(true);
    getApiProviderNames(token, aggId)
      .then(d => { if (d) setApiNames(d); })
      .finally(() => setLoadingApiNames(false));
  }, [aggId, token]);

  const getAliasMap = async () => {
    const cached = providerAliasCache[aggId];
    if (cached && Date.now() < cached.exp) return cached.val;
    const nested = await getProviderAliases(token);
    const map = nested?.[aggId] ?? {};
    providerAliasCache[aggId] = { val: map, exp: Date.now() + PROVIDER_ALIAS_TTL };
    return map;
  };

  const handleApiNameChange = async (name) => {
    setNoMatchFound(false);

    if (!matchViaAlias) {
      onChange('', name);
      return;
    }

    const aliasMap = await getAliasMap();
    const key = name.trim().toLowerCase();
    const aliasedDbName = aliasMap[key];

    const match = aliasedDbName
      ? dbProviders.find(p => p.name.toLowerCase() === aliasedDbName)
      : dbProviders.find(p => p.name.toLowerCase() === key);

    if (match) {
      onChange(String(match.id), name);
    } else {
      setNoMatchFound(true);
      onChange('', name);
    }
  };

  const handleCheckboxChange = (checked) => {
    setMatchViaAlias(checked);
    setNoMatchFound(false);
    onChange('', apiProviderName);
  };

  return (
    <div className="filter-row">
      <div className="filter-group">
        <label className="filter-label">API Provider</label>
        <SearchableSelect
          value={apiProviderName}
          onChange={handleApiNameChange}
          disabled={!aggId || loadingApiNames}
          options={apiNames.map(n => ({ value: n, label: n }))}
          placeholder={loadingApiNames ? 'Loading…' : '— Select —'}
        />
      </div>

      <div className="filter-group">
        <label className="filter-label">DB Provider</label>
        <SearchableSelect
          value={providerId}
          onChange={v => onChange(v, apiProviderName)}
          disabled={!aggId || loadingDbProviders || (matchViaAlias && !apiProviderName)}
          options={dbProviders.map(p => ({ value: p.id, label: p.name }))}
          placeholder={loadingDbProviders ? 'Loading…' : '— Select —'}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
          color: '#6b7280', marginTop: 5, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={matchViaAlias}
            onChange={e => handleCheckboxChange(e.target.checked)} />
          Match via alias
        </label>
        {noMatchFound && (
          <p style={{ fontSize: 10, color: '#d97706', margin: '3px 0 0', fontStyle: 'italic' }}>
            No automatic match found — uncheck to pick manually
          </p>
        )}
      </div>
    </div>
  );
}
